/**
 * Fetch-based memory SSE client.
 * Native EventSource reconnects on its own and cannot read HTTP status, so an
 * expired token in the URL reconnects forever. This reader closes on 401/403,
 * asks for a new Supabase access token, and retries with backoff.
 */

import { supabase } from "@/lib/supabase";

export const STREAM_RECONNECT_BASE_MS = 1000;
export const STREAM_RECONNECT_MAX_MS = 60_000;
export const STREAM_MAX_RECONNECTS = 10;

type SupabaseSessionResult = {
  data: { session: { access_token?: string | null } | null };
  error: { message?: string } | null;
};

type StreamAuthClient = {
  getSession: () => Promise<SupabaseSessionResult>;
  refreshSession: () => Promise<SupabaseSessionResult>;
};

export type StreamTokenRefresh =
  | { status: "refreshed"; token: string }
  | { status: "unchanged" }
  | { status: "failed" };

export function streamReconnectDelayMs(
  reconnectNumber: number,
  randomUnit: number = Math.random(),
): number {
  const exponent = Math.max(0, reconnectNumber - 1);
  const capped = Math.min(
    STREAM_RECONNECT_BASE_MS * 2 ** exponent,
    STREAM_RECONNECT_MAX_MS,
  );
  const unit = Math.min(1, Math.max(0, randomUnit));
  // Equal jitter keeps the delay between half and the full capped backoff.
  return Math.round(capped * (0.5 + unit * 0.5));
}

export function memoryStreamUrl(apiBaseUrl: string, spaceId?: string): string {
  const url = new URL(`${apiBaseUrl}/api/realtime/memories/stream`);
  if (spaceId) {
    url.searchParams.set("space_id", spaceId);
  }
  return url.toString();
}

/**
 * Read the current Supabase session, then refresh it when the access token
 * has not already changed. `failed` means the session could not be refreshed.
 * `unchanged` means refresh returned the same access token.
 */
export async function refreshStreamAccessToken(
  previousToken: string,
  auth: StreamAuthClient = supabase.auth,
): Promise<StreamTokenRefresh> {
  try {
    const current = await auth.getSession();
    const currentToken = current.data.session?.access_token;
    if (currentToken && currentToken !== previousToken) {
      return { status: "refreshed", token: currentToken };
    }
  } catch {
    // A locked or missing local session still gets one refresh attempt.
  }

  try {
    const refreshed = await auth.refreshSession();
    if (refreshed.error || !refreshed.data.session?.access_token) {
      return { status: "failed" };
    }
    const nextToken = refreshed.data.session.access_token;
    if (nextToken === previousToken) {
      return { status: "unchanged" };
    }
    return { status: "refreshed", token: nextToken };
  } catch {
    return { status: "failed" };
  }
}

type MemoryStreamAttempt =
  | { kind: "auth" }
  | { kind: "retryable"; opened: boolean }
  | { kind: "stopped"; opened: boolean };

export interface RunMemoryEventStreamOptions {
  apiBaseUrl: string;
  spaceId?: string;
  signal: AbortSignal;
  shouldContinue?: () => boolean;
  isHidden?: () => boolean;
  waitUntilVisible?: () => Promise<void>;
  sleep?: (ms: number) => Promise<void>;
  random?: () => number;
  maxReconnects?: number;
  fetchImpl?: typeof fetch;
  getAccessToken: () => Promise<string | null>;
  refreshAccessToken: (previousToken: string) => Promise<StreamTokenRefresh>;
  signOut: () => Promise<void>;
  onOpen?: () => void;
  onDown?: () => void;
  onReconnecting?: (info: { attempt: number; delayMs: number }) => void;
  onGaveUp?: () => void;
  onMessage?: (data: string) => void;
}

function abortError(): Error {
  const error = new Error("The operation was aborted");
  error.name = "AbortError";
  return error;
}

export function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === "AbortError";
}

function defaultIsHidden(): boolean {
  return typeof document !== "undefined" && document.hidden;
}

export function waitUntilDocumentVisible(signal: AbortSignal): Promise<void> {
  if (!defaultIsHidden()) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const finish = (error?: Error) => {
      document.removeEventListener("visibilitychange", onVisible);
      signal.removeEventListener("abort", onAbort);
      if (error) {
        reject(error);
      } else {
        resolve();
      }
    };
    const onAbort = () => finish(abortError());
    const onVisible = () => {
      if (!document.hidden) {
        finish();
      }
    };
    if (signal.aborted) {
      finish(abortError());
      return;
    }
    signal.addEventListener("abort", onAbort);
    document.addEventListener("visibilitychange", onVisible);
  });
}

function sleepWithAbort(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(abortError());
      return;
    }
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortError());
    };
    signal.addEventListener("abort", onAbort);
  });
}

async function pauseWhileHidden(
  signal: AbortSignal,
  isHidden: () => boolean,
  waitUntilVisible: () => Promise<void>,
): Promise<void> {
  while (isHidden()) {
    if (signal.aborted) {
      throw abortError();
    }
    await waitUntilVisible();
  }
}

async function readSse(
  response: Response,
  signal: AbortSignal,
  onMessage?: (data: string) => void,
): Promise<void> {
  const body = response.body;
  if (!body) {
    return;
  }
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const readChunk = (): Promise<ReadableStreamReadResult<Uint8Array>> => {
    if (signal.aborted) {
      return Promise.reject(abortError());
    }
    return new Promise((resolve, reject) => {
      const onAbort = () => {
        reader.cancel().catch(() => undefined);
        reject(abortError());
      };
      signal.addEventListener("abort", onAbort, { once: true });
      reader.read().then(
        (result) => {
          signal.removeEventListener("abort", onAbort);
          resolve(result);
        },
        (error) => {
          signal.removeEventListener("abort", onAbort);
          reject(error);
        },
      );
    });
  };

  try {
    while (!signal.aborted) {
      const { done, value } = await readChunk();
      if (done) {
        return;
      }
      buffer += decoder.decode(value, { stream: true });
      const frames = buffer.split("\n\n");
      buffer = frames.pop() ?? "";
      for (const frame of frames) {
        const data = frame
          .split("\n")
          .filter((line) => line.startsWith("data:"))
          .map((line) => line.slice(5).trimStart())
          .join("\n");
        if (data) {
          onMessage?.(data);
        }
      }
    }
    throw abortError();
  } finally {
    reader.releaseLock();
  }
}

async function discardUnreadBody(response: Response): Promise<void> {
  try {
    await response.body?.cancel();
  } catch {
    // Status was already read. A body error must not change that decision.
  }
}

async function openMemoryStream(
  options: RunMemoryEventStreamOptions,
  token: string,
): Promise<MemoryStreamAttempt> {
  const fetchImpl = options.fetchImpl ?? fetch;
  const url = memoryStreamUrl(options.apiBaseUrl, options.spaceId);
  let response: Response;
  try {
    response = await fetchImpl(url, {
      method: "GET",
      headers: {
        Accept: "text/event-stream",
        Authorization: `Bearer ${token}`,
        "X-Memara-Client": "ui",
      },
      cache: "no-store",
      signal: options.signal,
    });
  } catch (error) {
    if (isAbortError(error) || options.signal.aborted) {
      return { kind: "stopped", opened: false };
    }
    return { kind: "retryable", opened: false };
  }

  // Auth failures are decided by status alone. The body, including any
  // 401 detail string, is discarded unread.
  if (response.status === 401 || response.status === 403) {
    await discardUnreadBody(response);
    return { kind: "auth" };
  }
  if (!response.ok) {
    await discardUnreadBody(response);
    return { kind: "retryable", opened: false };
  }

  options.onOpen?.();
  try {
    await readSse(response, options.signal, options.onMessage);
  } catch (error) {
    if (isAbortError(error) || options.signal.aborted) {
      return { kind: "stopped", opened: true };
    }
    return { kind: "retryable", opened: true };
  }
  if (options.signal.aborted) {
    return { kind: "stopped", opened: true };
  }
  return { kind: "retryable", opened: true };
}

export async function runMemoryEventStream(
  options: RunMemoryEventStreamOptions,
): Promise<void> {
  const shouldContinue = options.shouldContinue ?? (() => true);
  const isHidden = options.isHidden ?? defaultIsHidden;
  const waitUntilVisible =
    options.waitUntilVisible ??
    (() => waitUntilDocumentVisible(options.signal));
  const sleep =
    options.sleep ?? ((ms: number) => sleepWithAbort(ms, options.signal));
  const random = options.random ?? Math.random;
  const maxReconnects = options.maxReconnects ?? STREAM_MAX_RECONNECTS;
  let reconnects = 0;
  let pendingToken: string | null = null;

  const stopped = () => options.signal.aborted || !shouldContinue();

  while (!stopped()) {
    try {
      await pauseWhileHidden(options.signal, isHidden, waitUntilVisible);
    } catch (error) {
      if (isAbortError(error) || stopped()) {
        return;
      }
      throw error;
    }
    if (stopped()) {
      return;
    }

    const token = pendingToken ?? (await options.getAccessToken());
    pendingToken = null;
    if (!token) {
      return;
    }
    if (stopped()) {
      return;
    }

    const attempt = await openMemoryStream(options, token);

    if (attempt.kind === "stopped" || stopped()) {
      if (attempt.kind !== "auth" && attempt.opened) {
        options.onDown?.();
      }
      return;
    }

    if (attempt.kind === "retryable" && attempt.opened) {
      options.onDown?.();
      // A live stream resets the failure budget. The drop below counts as one.
      reconnects = 0;
    }

    if (attempt.kind === "auth") {
      const refreshed = await options.refreshAccessToken(token);
      if (stopped()) {
        return;
      }
      if (refreshed.status === "failed") {
        try {
          await options.signOut();
        } catch {
          // Still stop. The session could not be refreshed.
        }
        options.onGaveUp?.();
        return;
      }
      if (refreshed.status === "unchanged") {
        // The server rejected the current token and refresh did not replace it.
        // Stop so the client does not reconnect with the same token.
        options.onGaveUp?.();
        return;
      }
      pendingToken = refreshed.token;
    }

    reconnects += 1;
    if (reconnects > maxReconnects) {
      options.onGaveUp?.();
      return;
    }

    const delayMs = streamReconnectDelayMs(reconnects, random());
    options.onReconnecting?.({ attempt: reconnects, delayMs });
    try {
      await pauseWhileHidden(options.signal, isHidden, waitUntilVisible);
      if (stopped()) {
        return;
      }
      await sleep(delayMs);
    } catch (error) {
      if (isAbortError(error) || stopped()) {
        return;
      }
      throw error;
    }
  }
}
